/**
 * Listado Cobros x Mayor — migración web de ares9750.
 *
 * Listado de los cobros aplicados a facturas del módulo Ventas x Mayor
 * en un rango de fechas, con filtro opcional por cliente. Cada línea es
 * un recibo aplicado a una cuota (un recibo puede pagar varias cuotas).
 * Exporta a Excel e imprime con fila de total.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { hayOtraMoneda, monedaLabel } from "lib/moneda";
import { imprimirListadoGrande } from "lib/impresionhtml";
import { etiquetaCuota } from "modules/ventasxmayor/impresionxmayor";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin cobros en el rango seleccionado
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

export default function ListadoCobrosXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);

  const [cobros, setCobros] = useState([]);
  const [total, setTotal] = useState(0);
  // Moneda en la que el backend ya expresó los importes.
  const [base, setBase] = useState("DOP");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_cobros_reporte", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        cliente: cliente?.id || "",
      });
      if (ok) {
        setCobros(data.cobros || []);
        setTotal(data.total || 0);
        setBase(data.base || "DOP");
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Columna solo si hay recibos de facturas en otra moneda (ver aging).
  const mezcla = hayOtraMoneda(cobros, base);
  const num = (v) => {
    const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };

  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}${
    cliente ? ` · ${cliente.descrip}` : ""
  } · Valores en ${monedaLabel(base)}`;

  // Mismas columnas en Excel e impresión; Moneda solo cuando hay mezcla,
  // igual que en pantalla.
  const cabeceras = [
    "Recibo#",
    "Fecha",
    "Fact#",
    ...(mezcla ? ["Moneda"] : []),
    "Cuota",
    "Cliente",
    "Forma",
    "Referencia",
    "Valor",
    "Suc.",
  ];
  const fila = (c, valor) => [
    c.id,
    c.fecha,
    c.nfact,
    ...(mezcla ? [monedaLabel(c.moneda)] : []),
    etiquetaCuota(c.cuotacompromiso),
    c.cliente,
    c.banco,
    c.referencia,
    valor,
    c.sucursal,
  ];
  const iValor = cabeceras.indexOf("Valor");
  const filaTotal = (valor) => {
    const f = cabeceras.map(() => "");
    f[iValor - 1] = "TOTAL";
    f[iValor] = valor;
    return f;
  };

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Listado Cobros x Mayor"],
      [subtitulo],
      [],
      cabeceras,
      ...cobros.map((c) => fila(c, num(c.valorpagado))),
      [],
      filaTotal(num(total)),
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Cobros x Mayor");
    XLSX.writeFile(libro, `ListadoCobrosXmayor_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirListadoGrande({
      titulo: "Listado Cobros x Mayor",
      subtitulo,
      cabeceras,
      filas: cobros.map((c) => fila(c, fmtMoney(c.valorpagado))),
      totales: filaTotal(`${monedaLabel(base)} ${fmtMoney(total)}`),
      numericas: [cabeceras.indexOf("Cuota"), iValor],
    });
  };

  const colMoneda = {
    field: "moneda",
    headerName: "Moneda",
    width: 90,
    valueFormatter: (value) => monedaLabel(value),
    description: `Moneda de emisión. Los importes se muestran en ${monedaLabel(base)}.`,
  };

  const columns = [
    { field: "id", headerName: "Recibo#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "nfact", headerName: "Fact#", width: 80 },
    ...(mezcla ? [colMoneda] : []),
    {
      field: "cuotacompromiso",
      headerName: "Cuota",
      width: 75,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    { field: "banco", headerName: "Forma", width: 120 },
    { field: "referencia", headerName: "Referencia", width: 130 },
    {
      field: "valorpagado",
      headerName: "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "sucursal", headerName: "Suc.", width: 70 },
  ];

  const hayDatos = cobros.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Listado Cobros x Mayor"
        subtitle="Cobros aplicados a facturas x mayor en el rango de fechas seleccionado"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                options={clientes}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="TOTAL COBRADO" value={`${monedaLabel(base)} ${fmtMoney(total)}`} />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={cobros}
              columns={columns}
              getRowId={(r) => `${r.id}-${r.nfact}-${r.cuotacompromiso}`}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
